import { Controller } from "@hotwired/stimulus"
import { useEscapeKey } from "../helpers/escape_key_mixin"

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type=\"hidden\"])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex=\"-1\"])"
].join(", ")

// Status dialog opened from a grid block. Escape and the close control
// return focus to that block.
export default class extends Controller {
  connect() {
    this.returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null
    this.onKeydown = (event) => this.trapTab(event)
    this.escapeKey = useEscapeKey(this, this.closeOnEscape)
    this.escapeKey.add()
    this.element.addEventListener("keydown", this.onKeydown)
    window.requestAnimationFrame(() => {
      const first = this.focusableElements()[0]
      if (first) first.focus({ preventScroll: true })
    })
  }

  disconnect() {
    this.escapeKey?.remove()
    this.element.removeEventListener("keydown", this.onKeydown)
    const back = this.returnTo
    this.returnTo = null
    if (back instanceof HTMLElement && document.contains(back)) {
      back.focus({ preventScroll: true })
    }
  }

  close(event) {
    event?.preventDefault()
    const frame = this.element.closest("turbo-frame")
    if (frame) frame.replaceChildren()
    else this.element.remove()
  }

  closeOnEscape(event) {
    if (event.key !== "Escape") return
    event.preventDefault()
    this.close(event)
  }

  focusableElements() {
    return [...this.element.querySelectorAll(FOCUSABLE_SELECTOR)].filter((el) => {
      if (!(el instanceof HTMLElement)) return false
      if (el.hasAttribute("disabled") || el.getAttribute("aria-hidden") === "true") return false
      if (typeof el.checkVisibility === "function") {
        return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
      }
      const style = window.getComputedStyle(el)
      return style.visibility !== "hidden" && style.display !== "none"
    })
  }

  trapTab(event) {
    if (event.key !== "Tab") return
    const focusables = this.focusableElements()
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    } else if (document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}
